import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import SectionProcessBg from '../components/SectionProcessBg';
import { fadeUp, staggerContainer } from '../motion/variants';

const HIGHLIGHTS = [
  '18 polymer segments',
  '42 country markets',
  'Regulatory timelines',
  'Strategic positioning',
];

const STATS = [
  {
    v: '18',
    l: 'Polymer segments',
    image: 'market-01-flexible-packaging.png',
    accent: 'gold',
  },
  {
    v: '42',
    l: 'Country markets',
    image: 'market-02-rigid-packaging.png',
    accent: 'blue',
  },
  {
    v: 'Q1',
    l: 'Quarterly updates',
    image: 'market-06-healthcare.png',
    accent: 'green',
  },
];

const TREND_CHIPS = [
  'rPET +24% YoY',
  'NIR-sortable blacks',
  'UV-stable film',
  'FDA 21 CFR ready',
];

const HERO_IMAGE = 'resource-trend-hero.png';

export default function ResourceTeaserSection() {
  const reduceMotion = useReducedMotion();
  const publicUrl = process.env.PUBLIC_URL || '';

  return (
    <section id="trends" className="sec sec-process-wrap resource-ht resource-ht--v2" data-palette="green">
      <SectionProcessBg variant="analytics" seed={4} />
      <div className="resource-ht__ambient" aria-hidden="true">
        <span className="resource-ht__shine" />
        <span className="resource-ht__orb resource-ht__orb--1" />
        <span className="resource-ht__orb resource-ht__orb--2" />
        <span className="resource-ht__mesh" />
      </div>

      <div className="sec-process-content resource-ht__inner">
        <div className="resource-ht__grid">
          <motion.div
            className="resource-ht__copy"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div className="resource-ht__eyebrow" variants={fadeUp}>
              <span className="resource-ht__eyebrow-dot" aria-hidden="true" />
              Market Intelligence
            </motion.div>
            <motion.h2 className="resource-ht__title" variants={fadeUp}>
              Masterbatch Trend
              <br />
              <em>Intelligence 2026–2030</em>
            </motion.h2>
            <motion.p className="resource-ht__desc" variants={fadeUp}>
              Detailed analysis covering polymer segments, country markets, regulatory timelines, and
              Auralux&apos;s strategic positioning for each trend vector.
            </motion.p>
            <motion.ul className="resource-ht__highlights" variants={fadeUp}>
              {HIGHLIGHTS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </motion.ul>
            <motion.div className="resource-ht__actions" variants={fadeUp}>
              <Link to="/contact?inquiry=intelligence-report" className="btn-p resource-ht__btn-primary">
                Request Full Report
              </Link>
              <Link to="/innovation" className="btn-g resource-ht__btn-secondary">
                Browse case studies →
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            className="resource-ht__bento"
            initial={reduceMotion ? false : { opacity: 0, y: 32, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.75, ease: [0.32, 0.72, 0, 1] }}
          >
            <div className="resource-ht__visual">
              <img
                src={`${publicUrl}/images/markets/${HERO_IMAGE}`}
                alt="Masterbatch trend intelligence — polymer market signals"
                loading="lazy"
                decoding="async"
              />
              <div className="resource-ht__visual-overlay">
                <span className="resource-ht__panel-tag">Trend Radar</span>
                <p className="resource-ht__visual-caption">Live signals across global converting</p>
              </div>
            </div>

            <div className="resource-ht__stats">
              {STATS.map((s, i) => (
                <motion.article
                  key={s.l}
                  className="resource-ht__stat"
                  data-accent={s.accent}
                  initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.55 }}
                >
                  <div className="resource-ht__stat-thumb">
                    <img src={`${publicUrl}/images/markets/${s.image}`} alt="" loading="lazy" />
                  </div>
                  <div className="resource-ht__stat-body">
                    <span className="resource-ht__stat-v">{s.v}</span>
                    <span className="resource-ht__stat-l">{s.l}</span>
                  </div>
                </motion.article>
              ))}
            </div>

            <div className="resource-ht__chips" aria-label="Trending topics">
              {TREND_CHIPS.map((chip) => (
                <span key={chip} className="resource-ht__chip">
                  {chip}
                </span>
              ))}
            </div>

            <svg className="resource-ht__chart" viewBox="0 0 320 80" fill="none" aria-hidden="true">
              <defs>
                <linearGradient id="resource-ht-chart-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#d5b036" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#d5b036" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                className="resource-ht__chart-area"
                d="M0,64 L40,58 L80,52 L120,40 L160,34 L200,24 L240,18 L280,12 L320,8 L320,80 L0,80 Z"
                fill="url(#resource-ht-chart-grad)"
              />
              <polyline
                className="resource-ht__chart-line"
                points="0,64 40,58 80,52 120,40 160,34 200,24 240,18 280,12 320,8"
              />
              <circle className="resource-ht__chart-dot" cx="320" cy="8" r="4" />
            </svg>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
